import type { CalendarEvent } from '@cornerstone/shared';

export const ROOM_TIMETABLE_TOKENS = {
  bgPanel: '#111317',
  bgPanel2: '#181b20',
  bgPanel3: '#1f2229',
  line: '#272a31',
  lineStrong: '#3a3e46',
  text: '#ecedef',
  textDim: '#8a8d93',
  textDimmer: '#565962',
  accent: '#F24822',
  accentSoft: 'rgba(242,72,34,0.18)',
  accentRing: 'rgba(242,72,34,0.55)',
  yellow: '#ffc14a',
  red: '#f87171',
  green: '#10b981',
  fontUi: '"Inter Tight", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", ui-monospace, monospace',
} as const;

export type RoomTimetableWindow = {
  /** Local-day midnight + windowStartHour, in unix ms. */
  startMs: number;
  /** Local-day midnight + windowEndHour, in unix ms. */
  endMs: number;
  durationMs: number;
};

/**
 * Compute today's fixed window in the viewer's local timezone.
 * Default 08:00–20:00 (12 h working window).
 */
export function computeWindow(
  nowMs: number,
  windowStartHour = 8,
  windowEndHour = 20,
): RoomTimetableWindow {
  const d = new Date(nowMs);
  const start = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    windowStartHour,
    0,
    0,
    0,
  ).getTime();
  const end = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    windowEndHour,
    0,
    0,
    0,
  ).getTime();
  return { startMs: start, endMs: end, durationMs: end - start };
}

/** Clamp an event's [startAt, endAt] to a window. Returns null if no overlap. */
export function clipEvent(
  event: CalendarEvent,
  win: RoomTimetableWindow,
): { startMs: number; endMs: number } | null {
  const s = Math.max(event.startAt, win.startMs);
  const e = Math.min(event.endAt, win.endMs);
  if (e <= s) return null;
  return { startMs: s, endMs: e };
}

/** Pixel-x for a unix-ms timestamp, given a window and total strip width. */
export function msToX(
  ms: number,
  win: RoomTimetableWindow,
  stripWidthPx: number,
): number {
  return ((ms - win.startMs) / win.durationMs) * stripWidthPx;
}

/** Status of an event relative to "now". */
export function eventState(
  event: CalendarEvent,
  nowMs: number,
): 'past' | 'live' | 'future' {
  if (event.endAt <= nowMs) return 'past';
  if (event.startAt <= nowMs && nowMs < event.endAt) return 'live';
  return 'future';
}

/** Format a unix-ms as HH:mm in the viewer's local tz. */
export function fmtHm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Hour tick positions inside the window (every full hour, inclusive of both ends). */
export function hourTicks(win: RoomTimetableWindow): number[] {
  return hourTicksEvery(win, 1);
}

/** Hour ticks subsampled to every `stepHours`. Always includes the start; the
 *  end tick is included only when it lands on the step grid. */
export function hourTicksEvery(
  win: RoomTimetableWindow,
  stepHours: number,
): number[] {
  const out: number[] = [];
  const startHour = new Date(win.startMs).getHours();
  const endHour = new Date(win.endMs).getHours();
  for (let h = startHour; h <= endHour; h += stepHours) {
    const tick = new Date(win.startMs);
    tick.setHours(h, 0, 0, 0);
    out.push(tick.getTime());
  }
  return out;
}
